import React, { useState, useContext, useEffect } from "react";
import { useLocation } from "react-router-dom";
import clsx from "clsx";
import {
  makeStyles,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  MenuItem,
  IconButton,
  Menu,
  Switch,
} from "@material-ui/core";
import MenuIcon from "@material-ui/icons/Menu";
import AccountCircle from "@material-ui/icons/AccountCircle";
import Brightness4Icon from "@material-ui/icons/Brightness4";

import MainListItems from "./MainListItems";
import NotificationsPopOver from "../components/NotificationsPopOver";
import UserModal from "../components/UserModal";
import { AuthContext } from "../context/Auth/AuthContext";
import BackdropLoading from "../components/BackdropLoading";
import { i18n } from "../translate/i18n";
import { useThemeContext } from "../context/DarkMode";
import gnsLogo from "../assets/gns-logo.png";

const drawerWidth = 240;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100vh",
    minHeight: "100dvh",
    [theme.breakpoints.down("sm")]: {
      height: "100dvh",
    },
  },
  toolbar: {
    minHeight: 58,
    paddingRight: 18,
  },
  toolbarIcon: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    minHeight: 72,
    padding: theme.spacing(0, 1.5),
    marginBottom: 16,
    color: "#fff",
    backgroundColor: "#20242d",
    borderBottom: "1px solid rgba(255,255,255,.08)",
  },
  brandCopy: {
    minWidth: 0,
    overflow: "hidden",
  },
  brandLogo: {
    width: 62,
    flex: "0 0 62px",
  },
  brandLogoImage: {
    display: "block",
    width: "100%",
    height: "auto",
    objectFit: "contain",
  },
  brandName: {
    color: "#fff",
    fontSize: 15,
    fontWeight: 800,
    lineHeight: 1.1,
    whiteSpace: "nowrap",
  },
  brandCaption: {
    color: "rgba(255,255,255,.5)",
    fontSize: 11,
    marginTop: 3,
    whiteSpace: "nowrap",
  },
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.type === "dark" ? "#263247" : "#e7edf5"}`,
    boxShadow: "none",
  },
  appBarShift: {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    [theme.breakpoints.down("xs")]: {
      marginLeft: 0,
      width: "100%",
    },
  },
  menuButton: {
    color: theme.palette.type === "dark" ? "#cbd5e1" : "#536176",
    marginRight: theme.spacing(1),
  },
  menuButtonHidden: {
    display: "none",
  },
  title: {
    flexGrow: 1,
    color: theme.palette.type === "dark" ? "#f8fafc" : "#17243f",
    fontSize: 14,
    fontWeight: 700,
    letterSpacing: 0,
  },
  drawerPaper: {
    position: "relative",
    whiteSpace: "nowrap",
    width: drawerWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    backgroundColor: theme.palette.background.paper,
    borderRight: `1px solid ${theme.palette.type === "dark" ? "#263247" : "#e7edf5"}`,
    display: "flex",
    flexDirection: "column",
    [theme.breakpoints.down("xs")]: {
      width: "min(86vw, 320px)",
      maxWidth: "320px",
    },
  },
  drawerPaperClose: {
    overflowX: "hidden",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    width: theme.spacing(7),
    [theme.breakpoints.up("sm")]: {
      width: theme.spacing(9),
    },
  },
  appBarSpacer: {
    minHeight: 58,
  },
  content: {
    flex: 1,
    overflow: "auto",
    minWidth: 0,
    [theme.breakpoints.down("sm")]: {
      overflow: "hidden",
    },
  },
  container: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  paper: {
    padding: theme.spacing(2),
    display: "flex",
    overflow: "auto",
    flexDirection: "column",
  },
  switch: {
    transform: "scale(0.8)",
  },
  iconButton: {
    color: theme.palette.type === "dark" ? "#cbd5e1" : "#536176",
  },
  themeSwitchContainer: {
    display: "flex",
    alignItems: "center",
  },
  themeIcon: {
    color: theme.palette.type === "dark" ? "#cbd5e1" : "#718096",
  },
  drawerCollapsedSpacer: {
    minHeight: 58,
    flex: "0 0 58px",
  },
  ticketTitle: {
    fontSize: 18,
    fontWeight: 800,
  },
  ticketStatus: {
    alignItems: "center",
    border: `1px solid ${theme.palette.type === "dark" ? "#355339" : "#b9e4c0"}`,
    borderRadius: 8,
    color: theme.palette.type === "dark" ? "#a7e5b2" : "#18733b",
    display: "flex",
    fontSize: 12,
    fontWeight: 700,
    gap: theme.spacing(0.75),
    marginRight: theme.spacing(1.5),
    padding: theme.spacing(0.75, 1.25),
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  ticketStatusDot: {
    backgroundColor: "#1d9a4b",
    borderRadius: "50%",
    height: 7,
    width: 7,
  },
  pageToolbarActions: {
    alignItems: "center",
    display: "flex",
    flexShrink: 1,
    minWidth: 0,
    "& > *": {
      minWidth: 0,
    },
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  drawerContent: {
    flex: 1,
    overflowY: "auto",
    overflowX: "hidden",
    paddingTop: theme.spacing(1),
  },
  drawerFooter: {
    borderTop: `1px solid ${theme.palette.type === "dark" ? "#263247" : "#e7edf5"}`,
    padding: theme.spacing(1.25),
  },
  userButton: {
    alignItems: "center",
    background: "transparent",
    border: 0,
    color: theme.palette.text.primary,
    cursor: "pointer",
    display: "flex",
    gap: theme.spacing(1),
    padding: theme.spacing(0.75),
    textAlign: "left",
    width: "100%",
    "&:hover": {
      backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.06)" : "#f4f7fb",
    },
  },
  userName: {
    fontSize: 13,
    fontWeight: 700,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  userRole: {
    color: theme.palette.type === "dark" ? "#9aa5b8" : "#7b8798",
    fontSize: 11,
    marginTop: 2,
    textTransform: "capitalize",
  },
  drawerTheme: {
    alignItems: "center",
    color: theme.palette.type === "dark" ? "#cbd5e1" : "#536176",
    display: "flex",
    fontSize: 12,
    fontWeight: 600,
    justifyContent: "space-between",
    marginTop: theme.spacing(1),
  },
}));

const LoggedInLayout = ({ children }) => {
  const classes = useStyles();
  const location = useLocation();
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const { handleLogout, loading } = useContext(AuthContext);
  const isMobileViewport = () => window.matchMedia("(max-width: 599.95px)").matches;
  const [drawerOpen, setDrawerOpen] = useState(() => !isMobileViewport());
  const [drawerVariant, setDrawerVariant] = useState(() => (
    isMobileViewport() ? "temporary" : "permanent"
  ));
  const { user } = useContext(AuthContext);
  const { darkMode, toggleTheme } = useThemeContext();
  const isTicketsView = location.pathname === "/tickets" || location.pathname.startsWith("/tickets/");
  const pageTitles = {
    "/": "Panel operativo",
    "/connections": i18n.t("connections.title"),
    "/contacts": i18n.t("contacts.title"),
    "/users": i18n.t("users.title"),
    "/quickAnswers": i18n.t("quickAnswers.title"),
    "/Queues": i18n.t("queues.title"),
    "/campaigns": "Campañas",
    "/reports": "Informes operativos",
    "/Settings": i18n.t("settings.title"),
  };
  const pageTitle = isTicketsView ? "Bandeja" : pageTitles[location.pathname] || "GNS WhatsApp";

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 599.95px)");
    const syncDrawerMode = (event) => {
      setDrawerVariant(event.matches ? "temporary" : "permanent");
      setDrawerOpen(!event.matches);
    };

    const addListener = mediaQuery.addEventListener
      ? () => mediaQuery.addEventListener("change", syncDrawerMode)
      : () => mediaQuery.addListener(syncDrawerMode);
    const removeListener = mediaQuery.removeEventListener
      ? () => mediaQuery.removeEventListener("change", syncDrawerMode)
      : () => mediaQuery.removeListener(syncDrawerMode);

    addListener();
    return removeListener;
  }, []);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
    setMenuOpen(true);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuOpen(false);
  };

  const handleOpenUserModal = () => {
    setUserModalOpen(true);
    handleCloseMenu();
  };

  const handleClickLogout = () => {
    handleCloseMenu();
    handleLogout();
  };

  const drawerClose = () => {
    if (document.body.offsetWidth < 600) {
      setDrawerOpen(false);
    }
  };

  if (loading) {
    return <BackdropLoading />;
  }

  return (
    <div className={classes.root}>
      <Drawer
        variant={drawerVariant}
        className={drawerOpen ? classes.drawerPaper : classes.drawerPaperClose}
        classes={{
          paper: clsx(
            classes.drawerPaper,
            !drawerOpen && classes.drawerPaperClose
          ),
        }}
        open={drawerOpen}
      >
        {drawerOpen ? (
          <div className={classes.toolbarIcon}>
            <div className={classes.brandLogo}>
              <img className={classes.brandLogoImage} src={gnsLogo} alt="GNS" />
            </div>
            <div className={classes.brandCopy}>
              <div className={classes.brandName}>GNS WhatsApp</div>
              <div className={classes.brandCaption}>Centro de atención</div>
            </div>
          </div>
        ) : (
          <div className={classes.drawerCollapsedSpacer} />
        )}
        <List className={classes.drawerContent}>
          <MainListItems drawerClose={drawerClose} />
        </List>
        <div className={classes.drawerFooter}>
          <button type="button" className={classes.userButton} onClick={handleMenu}>
            <AccountCircle />
            <div className={classes.brandCopy}>
              <div className={classes.userName}>{user?.name || "Mi cuenta"}</div>
              <div className={classes.userRole}>{user?.profile || "usuario"}</div>
            </div>
          </button>
          <div className={classes.drawerTheme}>
            <span>Modo oscuro</span>
            <Switch checked={darkMode} onChange={toggleTheme} color="primary" className={classes.switch} />
          </div>
        </div>
      </Drawer>
      <UserModal
        open={userModalOpen}
        onClose={() => setUserModalOpen(false)}
        userId={user?.id}
      />
      <AppBar
        position="absolute"
        className={clsx(classes.appBar, drawerOpen && classes.appBarShift)}
      >
        <Toolbar variant="dense" className={classes.toolbar}>
          <IconButton
            edge="start"
            aria-label="open drawer"
            onClick={() => setDrawerOpen(!drawerOpen)}
            className={classes.menuButton}
          >
            <MenuIcon />
          </IconButton>
          <Typography
            component="h1"
            variant="h6"
            noWrap
            className={clsx(classes.title, isTicketsView && classes.ticketTitle)}
          >
            {pageTitle}
          </Typography>

          <div id="page-toolbar-actions" className={classes.pageToolbarActions} />

          {isTicketsView && (
            <div className={classes.ticketStatus}>
              <span className={classes.ticketStatusDot} /> Atención operativa
            </div>
          )}

          <div className={classes.themeSwitchContainer}>
            <Brightness4Icon className={classes.themeIcon} />
            <Switch checked={darkMode} onChange={toggleTheme} color="default" className={classes.switch} />
          </div>

          {user.id && (
            <NotificationsPopOver className={classes.iconButton} />
          )}

          <div>
            <IconButton
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}
              className={classes.iconButton}
            >
              <AccountCircle />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              getContentAnchorEl={null}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={menuOpen}
              onClose={handleCloseMenu}
            >
              <MenuItem onClick={handleOpenUserModal}>
                {i18n.t("mainDrawer.appBar.user.profile")}
              </MenuItem>
              <MenuItem onClick={handleClickLogout}>
                {i18n.t("mainDrawer.appBar.user.logout")}
              </MenuItem>
            </Menu>
          </div>
        </Toolbar>
      </AppBar>
      <main className={classes.content}>
        <div className={classes.appBarSpacer} />
        {children ? children : null}
      </main>
    </div>
  );
};

export default LoggedInLayout;
